import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X, Clapperboard, Search, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { FILMOGRAPHY } from "@/constants/filmography";

const CHIPS = [
  { id: "all", label: "All" },
  { id: "film", label: "Films" },
  { id: "ott", label: "OTT Series" },
  { id: "gov", label: "Government" },
];

const KIND_LABEL = { film: "Feature Film", ott: "OTT Series", gov: "Government" };
const ease = [0.16, 1, 0.3, 1];

const Poster = ({ item }) => {
  const [err, setErr] = useState(false);
  if (!item.poster || err) {
    return (
      <div className="w-full aspect-[2/3] bg-[#0F2A43] flex flex-col items-center justify-center p-4 text-center">
        <Clapperboard size={22} strokeWidth={1.2} className="text-white/50" />
        <p className="mt-3 font-serif text-[15px] leading-snug text-white uppercase">
          {item.title}{item.season ? ` — ${item.season}` : ""}
        </p>
        <p className="mt-2 font-mono text-[7px] tracking-[0.3em] uppercase text-white/40">Official Archive</p>
      </div>
    );
  }
  return (
    <img
      src={item.poster}
      alt={`${item.title} official poster`}
      loading="lazy"
      onError={() => setErr(true)}
      className="w-full aspect-[2/3] object-cover group-hover:scale-[1.04] transition-transform duration-300"
    />
  );
};

const ProjectModal = ({ item, onClose }) => (
  <AnimatePresence>
    {item && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#0F2A43]/45 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 50, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 40, opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.35, ease }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-4xl bg-white rounded-[20px] overflow-hidden max-h-[90vh] overflow-y-auto border border-[#E5E7EB] shadow-[0_50px_120px_-20px_rgba(15,42,67,0.4)]"
          data-testid="aero-filmography-modal"
        >
          <div className="grid md:grid-cols-12">
            <div className="md:col-span-5 bg-[#111827]">
              <Poster item={item} />
            </div>
            <div className="md:col-span-7 p-8 md:p-12">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[13px] tracking-[0.28em] uppercase text-[#4F6D8A]">{KIND_LABEL[item.kind]}</p>
                  <h3 className="mt-2 font-serif text-3xl md:text-4xl text-[#111827] leading-tight">
                    {item.title}
                    {item.season && <span className="text-[#4F6D8A]"> — {item.season}</span>}
                  </h3>
                </div>
                <button data-testid="aero-filmography-modal-close" onClick={onClose} aria-label="Close" className="text-[#6B7280] hover:text-[#0F2A43] transition-colors shrink-0">
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>
              <div className="mt-7 space-y-3 font-mono text-[13px] tracking-[0.16em] uppercase">
                <p className="text-[#6B7280]">ROLE — <span className="text-[#0F2A43] font-bold">AERIAL CINEMATOGRAPHY</span></p>
                {item.platform && <p className="text-[#6B7280]">PLATFORM — <span className="text-[#111827]">{item.platform}</span></p>}
                {item.director && <p className="text-[#6B7280]">DIRECTOR — <span className="text-[#111827]">{item.director}</span></p>}
              </div>
              <a
                data-testid="aero-filmography-trailer"
                href={`https://www.youtube.com/results?search_query=${encodeURIComponent(item.title + (item.season ? " " + item.season : "") + " official trailer")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-3 bg-[#0F2A43] text-white font-mono text-[13px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full shadow-[0_15px_35px_-12px_rgba(15,42,67,0.5)] hover:-translate-y-0.5 hover:bg-[#16395C] transition-all duration-300"
              >
                <Play size={12} fill="currentColor" /> Watch Trailer
              </a>
              <div className="mt-9">
                <p className="font-mono text-[13px] tracking-[0.28em] uppercase text-[#4F6D8A] mb-3">Behind The Scenes</p>
                <div className="w-full aspect-video rounded-xl border border-dashed border-[#4F6D8A]/40 bg-[#F8F6F2] flex items-center justify-center">
                  <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-[#6B7280]">BTS Stills — Coming Soon</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const AeroFilmography = () => {
  const [chip, setChip] = useState("all");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);
  const rowRef = useRef(null);

  const q = query.trim().toLowerCase();
  const items = FILMOGRAPHY.filter(
    (f) => (chip === "all" || f.kind === chip) && (!q || f.title.toLowerCase().includes(q))
  );
  const scrollBy = (dir) => rowRef.current?.scrollBy({ left: dir * 440, behavior: "smooth" });

  return (
    <section id="filmography" data-testid="aero-filmography" className="bg-[#F8F6F2] py-[100px] md:py-[120px] overflow-hidden">
      <div className="max-w-[1320px] mx-auto px-6 md:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease }}
        >
          <p className="font-mono text-[14px] tracking-[0.3em] uppercase text-[#4F6D8A]">Featured Work</p>
          <h2 className="mt-4 font-serif text-5xl md:text-[56px] leading-none text-[#111827]">Filmography</h2>
          <span className="mt-5 block w-14 h-[2px] bg-[#4F6D8A]" />
        </motion.div>

        {/* Chips + Search */}
        <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <button
                key={c.id}
                data-testid={`aero-filmography-chip-${c.id}`}
                onClick={() => setChip(c.id)}
                className={`font-mono text-[13px] tracking-[0.16em] uppercase px-6 py-3 rounded-full transition-all duration-300 ${
                  chip === c.id
                    ? "bg-[#0F2A43] text-white shadow-[0_12px_30px_-10px_rgba(15,42,67,0.5)]"
                    : "bg-white border border-[#E5E7EB] text-[#6B7280] hover:border-[#0F2A43]/40 hover:text-[#0F2A43]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="relative md:w-80">
            <Search size={15} strokeWidth={1.5} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input
              data-testid="aero-filmography-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects..."
              className="w-full bg-white border border-[#E5E7EB] focus:border-[#4F6D8A] outline-none rounded-full pl-12 pr-5 py-3.5 text-[15px] text-[#111827] placeholder:text-[#9CA3AF] transition-colors"
            />
          </div>
        </div>

        {/* Horizontal scroll row */}
        {items.length === 0 ? (
          <p className="mt-20 text-center font-mono text-[13px] tracking-[0.28em] uppercase text-[#6B7280]">
            "{query}" — koi project nahi mila
          </p>
        ) : (
          <div className="relative mt-12">
            <button
              data-testid="aero-filmography-prev"
              onClick={() => scrollBy(-1)}
              aria-label="Scroll left"
              className="hidden md:flex absolute -left-5 top-[38%] z-10 w-11 h-11 rounded-full bg-white border border-[#E5E7EB] shadow-[0_10px_30px_-10px_rgba(15,42,67,0.3)] items-center justify-center text-[#0F2A43] hover:bg-[#0F2A43] hover:text-white transition-all duration-300"
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button
              data-testid="aero-filmography-next"
              onClick={() => scrollBy(1)}
              aria-label="Scroll right"
              className="hidden md:flex absolute -right-5 top-[38%] z-10 w-11 h-11 rounded-full bg-white border border-[#E5E7EB] shadow-[0_10px_30px_-10px_rgba(15,42,67,0.3)] items-center justify-center text-[#0F2A43] hover:bg-[#0F2A43] hover:text-white transition-all duration-300"
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>

            <div
              ref={rowRef}
              data-testid="aero-filmography-row"
              className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {items.map((item, i) => (
                <motion.button
                  key={item.title + (item.season || "")}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-20px" }}
                  transition={{ duration: 0.5, delay: Math.min(i, 6) * 0.05, ease }}
                  onClick={() => setActive(item)}
                  data-testid={`aero-film-card-${i}`}
                  className="group shrink-0 w-[155px] md:w-[190px] snap-start text-left"
                >
                  <div className="relative rounded-[18px] overflow-hidden bg-white border border-[#E5E7EB] shadow-[0_8px_30px_-12px_rgba(15,42,67,0.15)] group-hover:shadow-[0_25px_55px_-15px_rgba(15,42,67,0.4)] group-hover:border-[#4F6D8A]/60 group-hover:-translate-y-1 transition-all duration-300">
                    <Poster item={item} />
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#0F2A43]/40">
                      <span className="inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-white">
                        View Project <ArrowRight size={11} />
                      </span>
                    </span>
                  </div>
                  <p className="mt-3.5 font-medium text-[15px] text-[#111827] leading-snug group-hover:text-[#4F6D8A] transition-colors duration-300">
                    {item.title}{item.season ? ` — ${item.season}` : ""}
                  </p>
                  <p className="mt-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-[#6B7280]">
                    {KIND_LABEL[item.kind]} · Aerial Cinematography
                  </p>
                </motion.button>
              ))}
            </div>
          </div>
        )}
      </div>
      <ProjectModal item={active} onClose={() => setActive(null)} />
    </section>
  );
};
